import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** A door: where a check-in station stands (ADR 0078). Named by the church, at a campus or any, kept like a room. */
export interface ManagedDoor {
  readonly id: string;
  readonly name: string;
  readonly campusId: string | null;
  readonly campusName: string | null;
  readonly isActive: boolean;
  readonly sortOrder: number;
}

/** Every door of the church, active and archived, in the order the station lists them. A church has a handful. */
export function useManagedDoors() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["check-in-doors", tenantId, "all"],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ManagedDoor[]> => {
      const { data, error } = await supabase
        .from("check_in_doors")
        .select("id, name, campus_id, is_active, sort_order, campuses(name)")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((d) => ({
        id: d.id,
        name: d.name,
        campusId: d.campus_id,
        campusName: d.campuses?.name ?? null,
        isActive: d.is_active,
        sortOrder: d.sort_order,
      }));
    },
  });
}

/** The doors the station offers: active ones, in order. */
export function useDoors() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["check-in-doors", tenantId, "active"],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ManagedDoor[]> => {
      const { data, error } = await supabase
        .from("check_in_doors")
        .select("id, name, campus_id, is_active, sort_order, campuses(name)")
        .eq("tenant_id", tenantId ?? "")
        .eq("is_active", true)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((d) => ({
        id: d.id,
        name: d.name,
        campusId: d.campus_id,
        campusName: d.campuses?.name ?? null,
        isActive: d.is_active,
        sortOrder: d.sort_order,
      }));
    },
  });
}

export interface DoorFields {
  readonly name: string;
  readonly campusId: string | null;
  readonly sortOrder: number;
}

export const EMPTY_DOOR: DoorFields = { name: "", campusId: null, sortOrder: 0 };

/** Problems with the form, in words, keyed by field. An empty object means it can be saved. */
export function validateDoor(
  fields: DoorFields,
  otherNames: readonly string[],
): Partial<Record<"name", string>> {
  const name = fields.name.trim();
  if (name.length === 0) return { name: "Give the door a name." };
  if (name.length > 80) return { name: "Keep the name to 80 characters or fewer." };
  if (otherNames.some((n) => n.trim().toLowerCase() === name.toLowerCase()))
    return { name: "Another door already has that name." };
  return {};
}

function useDoorInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  // The station's own list shares the first two key parts, so one call refreshes both.
  return () => client.invalidateQueries({ queryKey: ["check-in-doors", tenantId] });
}

export function useSaveDoor() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useDoorInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly doorId: string | null; readonly fields: DoorFields }) => {
      const f = input.fields;
      const { error } = await supabase.rpc("set_check_in_door", {
        p_tenant_id: tenantId ?? "",
        p_name: f.name.trim(),
        ...(input.doorId === null ? {} : { p_door_id: input.doorId }),
        ...(f.campusId === null ? {} : { p_campus_id: f.campusId }),
        p_sort_order: f.sortOrder,
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: invalidate,
  });
}

export function useSetDoorActive() {
  const supabase = useSupabase();
  const invalidate = useDoorInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly doorId: string; readonly isActive: boolean }) => {
      const { error } = await supabase.rpc("set_check_in_door_active", {
        p_door_id: input.doorId,
        p_is_active: input.isActive,
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: invalidate,
  });
}
